import React from 'react'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { DownloadArrowSVG } from 'src/components/Icons/Basic'
import { PERMISSIONS } from 'src/config/roleConfig'
import {
  ResourceWithAuthz,
  useHasRequiredCapabilities,
  useHasRequiredPermissions,
} from 'src/app/AppAuth/AppAuth.hooks'
import Dropdown from 'react-bootstrap/Dropdown'
import {
  BasicDropdownMenu,
  BasicDropdownMenuItem,
} from '../BasicDropdown/BasicDropdown.styles'
import { Tooltip, TooltipPositions } from '../Tooltip/Tooltip'
import { IconButton, IconButtonVariant } from './IconButton'
import { DropdownToggleButton } from './DropdownToggleButton'

export interface ExportOption {
  label: string
  onClick: () => void
  testId?: string
}

interface ExportIconButtonBaseProps {
  isDisabled?: boolean
  tooltipText?: string
  requiredPermissions?: PERMISSIONS[]
  requiredCapabilities?: CAPABILITY[]
  resource?: ResourceWithAuthz
  id?: string
  testId?: string
}

// The two variants are mutually exclusive: a plain button that runs one action, or a
// dropdown of at least one option. Allowing both would render an export button that
// silently ignores `onClick`, and allowing neither would render one that does nothing.
interface ExportIconButtonActionProps extends ExportIconButtonBaseProps {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void
  options?: never
}

interface ExportIconButtonDropdownProps extends ExportIconButtonBaseProps {
  onClick?: never
  options: readonly [ExportOption, ...ExportOption[]]
}

export type ExportIconButtonProps =
  | ExportIconButtonActionProps
  | ExportIconButtonDropdownProps

export const ExportIconButton = ({
  onClick,
  options,
  isDisabled = false,
  tooltipText = 'Export',
  requiredPermissions = [PERMISSIONS.READ],
  requiredCapabilities,
  resource,
  id = 'export-button',
  testId,
}: ExportIconButtonProps): JSX.Element => {
  const hasPermission = useHasRequiredPermissions(requiredPermissions)
  const hasCapabilities = useHasRequiredCapabilities(
    requiredCapabilities,
    resource,
  )
  const isAuthorized = requiredCapabilities ? hasCapabilities : hasPermission
  const isButtonDisabled = isDisabled || !isAuthorized
  const generatedTooltipId = React.useId()
  const tooltipId = id ?? generatedTooltipId

  const tooltipBody = isAuthorized
    ? tooltipText
    : 'You do not have permission to perform this action'

  if (options) {
    return (
      <Tooltip
        id={tooltipId}
        tooltipBody={tooltipBody}
        placement={TooltipPositions.top}
        disabled={isButtonDisabled && !isDisabled}
      >
        <Dropdown>
          <Dropdown.Toggle
            as={DropdownToggleButton}
            disabled={isButtonDisabled}
            aria-label={tooltipText}
            data-testid={testId}
          >
            <DownloadArrowSVG />
          </Dropdown.Toggle>
          <BasicDropdownMenu>
            {options.map((option, idx) => (
              <BasicDropdownMenuItem
                key={option.label || idx}
                data-testid={option.testId}
                onClick={() => option.onClick()}
              >
                {option.label}
              </BasicDropdownMenuItem>
            ))}
          </BasicDropdownMenu>
        </Dropdown>
      </Tooltip>
    )
  }

  return (
    <Tooltip
      id={tooltipId}
      tooltipBody={tooltipBody}
      placement={TooltipPositions.top}
      disabled={isButtonDisabled && !isDisabled}
    >
      <IconButton
        variant={IconButtonVariant.icon}
        onClick={onClick}
        disabled={isButtonDisabled}
        aria-label={tooltipText}
        data-testid={testId}
      >
        <DownloadArrowSVG />
      </IconButton>
    </Tooltip>
  )
}
